import React from "react";
import { useTranslation } from "react-i18next";
import InitialsAvatar from "../../../components/initials-avatar/InitialsAvatar";
import { ConsumerType, CustomerMeterNumber, PendingStatus, ResidencyStatus } from "../../../../../model/energy-profile";
import { CloseIcon } from "../../../../../assets/icons";
import { FeatureFlagName } from "../../../../../tenant/features/featureFlags";
import { useFeatureFlag } from "../../../../../tenant/features/useFeatureFlag";
import styles from "./meter-details-modal.module.scss";

interface MeterDetailsModalProps {
	isOpen: boolean;
	meter: CustomerMeterNumber | null;
	onClose: () => void;
}

export const MeterDetailsModal: React.FC<MeterDetailsModalProps> = ({ isOpen, meter, onClose }) => {
	const { t } = useTranslation();
	const isRemoteTokenLoadEnabled = useFeatureFlag(FeatureFlagName.REMOTE_TOKEN_LOAD);

	if (!isOpen || !meter) return null;

	const isPostpaid = meter.consumerType === ConsumerType.Postpaid;

	const serviceType =
		meter.consumerType === ConsumerType.Prepaid
			? t("common.labels.prepaid")
			: isPostpaid
				? t("common.labels.postpaid")
				: "";

	const residency =
		meter.residencyStatus === ResidencyStatus.Tenant
			? t("common.labels.tenant")
			: meter.residencyStatus === ResidencyStatus.Landlord
				? t("common.labels.landlord")
				: "";

	const rows: { label: string; value: string }[] = [
		{ label: t("common.labels.serviceType"), value: serviceType },
		{ label: t("energyManagement.energyProfile.accountMeterNumber"), value: isPostpaid ? meter.accountNumber : meter.meterNumber },
		...(meter.residencyStatus != null ? [{ label: t("energyManagement.energyProfile.residencyStatus"), value: residency }] : []),
		{ label: t("common.labels.status"), value: meter.pendingStatus === PendingStatus.Pending ? t("common.labels.pending") : t("common.labels.active") },
		...(isRemoteTokenLoadEnabled
			? [{ label: t("common.labels.remoteTokenLoad"), value: meter.preferRemoteTokenLoad ? t("common.actions.yes") : t("common.actions.no") }]
			: []),
	];

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) onClose();
	};

	return (
		<div className={styles.backdrop} onClick={handleBackdropClick}>
			<div className={styles.wrapper}>
				<div className={styles.modal}>
					<div className={styles.header}>
						<InitialsAvatar name={meter.name} index={0} size={48} fontSize={16} />
						<span className={styles.name}>{meter.name}</span>
					</div>

					<div className={styles.divider} />

					<div className={styles.details}>
						{rows.map(row => (
							<div className={styles.detailRow} key={row.label}>
								<span className={styles.detailLabel}>{row.label}</span>
								<span className={styles.detailValue}>{row.value}</span>
							</div>
						))}
					</div>
				</div>

				<button className={styles.closeButton} onClick={onClose} type="button" aria-label="Close">
					<CloseIcon />
				</button>
			</div>
		</div>
	);
};

export default MeterDetailsModal;
